/* ONE TUNNE: links from the product rating to the Judge.me reviews section.
 *
 * [data-reviews-link] scrolls to #reviews, smoothly unless prefers-reduced-motion is set, and
 * keeps the section heading clear of a sticky header. [data-write-review] then opens the Judge.me
 * form: the widget is drawn by the app some time after load, so its "write a review" button is
 * polled for every 200 ms for up to 8 s. If it never shows up, the scroll alone remains. Without
 * JavaScript the links are plain #reviews anchors.
 *
 * It also hands the colour scheme of the reviews section to the Judge.me form, see themeForm().
 */
(() => {
  if (window.otReviewsLink) return;
  window.otReviewsLink = true;

  const WRITE_BUTTONS = ['.jm-action-buttons__button', '.jdgm-write-rev-link'];
  const POLL_MS = 200;
  const POLL_LIMIT_MS = 8000;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const headerOffset = () => {
    const header = document.querySelector('.section-header');
    if (!header) return 0;
    const position = getComputedStyle(header).position;
    return position === 'sticky' || position === 'fixed' ? header.getBoundingClientRect().height : 0;
  };

  const scrollToSection = (section) => {
    const top = section.getBoundingClientRect().top + window.scrollY - headerOffset() - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  const findWriteButton = (section) => {
    for (const selector of WRITE_BUTTONS) {
      const button = section.querySelector(selector);
      if (button && button.getClientRects().length) return button;
    }
    return null;
  };

  const openForm = (section) => {
    const started = Date.now();
    const tick = () => {
      const button = findWriteButton(section);
      if (button) {
        button.click();
        return;
      }
      if (Date.now() - started < POLL_LIMIT_MS) setTimeout(tick, POLL_MS);
    };
    tick();
  };

  /* Judge.me appends the write-a-review form to <body>, outside the section and its colour
     scheme. The content box of the form gets the scheme class of the section, so the theme
     tokens of that scheme apply inside it. */
  const themeForm = () => {
    const section = document.querySelector('[data-judgeme-reviews][data-scheme-class]');
    const box = document.querySelector('.jdgm-write-review-modal .jdgm-write-review-modal__content');
    if (!section || !box) return false;
    box.classList.add(section.dataset.schemeClass);
    return true;
  };

  /* Watched for the life of the page: Judge.me may build the form anew for every opening. Only
     direct children of <body> are observed, so the cost is a check per appended node. */
  const watchForm = () => {
    themeForm();
    new MutationObserver(themeForm).observe(document.body, { childList: true });
  };

  /* The Judge.me script marks the badge stars up as a button (role="button", tabindex="0") when it
     draws them. Inside the rating link that is a second, nested control and an extra tab stop, so
     the attributes are taken off again whenever the script puts them back. */
  const STAR_CONTROL = '[role="button"], [tabindex]';
  const flattenBadge = (link) => {
    link.querySelectorAll(STAR_CONTROL).forEach((el) => {
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
    });
  };

  const watchBadges = () => {
    document.querySelectorAll('.product-rating__summary').forEach((link) => {
      flattenBadge(link);
      new MutationObserver(() => flattenBadge(link)).observe(link, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['role', 'tabindex'],
      });
    });
  };

  const start = () => {
    watchForm();
    watchBadges();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* Capture phase: the stars of the Judge.me badge have their own click handler that scrolls to
     the widget container, past the section heading. The theme link handles the click first and
     stops it there. */
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('[data-reviews-link]');
      if (!link) return;
      const section = document.getElementById('reviews');
      if (!section) return;
      event.preventDefault();
      event.stopPropagation();
      scrollToSection(section);
      if (link.hasAttribute('data-write-review')) openForm(section);
    },
    true
  );
})();
